import { useEffect } from "react";
import { track } from "@/lib/tracking";

/**
 * Hook que mede LCP/CLS/INP/FCP nativos via PerformanceObserver e envia
 * para o dataLayer. Substitui a lib `web-vitals` sem adicionar dependência.
 * Roda 1× no mount do app (idempotente, observers únicos).
 */
export function usePerfObserver() {
  useEffect(() => {
    if (typeof window === "undefined" || !("PerformanceObserver" in window))
      return;

    let cls = 0;
    let lcpReported = false;

    const lcpObs = new PerformanceObserver(list => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & {
        renderTime?: number;
        loadTime?: number;
      };
      if (!last) return;
      const value = (last.renderTime || last.loadTime || last.startTime) | 0;
      if (!lcpReported) {
        track("web_vital_lcp", { value });
        lcpReported = true;
      }
    });

    const clsObs = new PerformanceObserver(list => {
      for (const entry of list.getEntries() as Array<
        PerformanceEntry & { value: number; hadRecentInput?: boolean }
      >) {
        if (!entry.hadRecentInput) cls += entry.value;
      }
    });

    const fcpObs = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          track("web_vital_fcp", { value: entry.startTime | 0 });
        }
      }
    });

    const inpObs = new PerformanceObserver(list => {
      let worst = 0;
      for (const entry of list.getEntries() as Array<
        PerformanceEntry & { duration: number }
      >) {
        if (entry.duration > worst) worst = entry.duration;
      }
      if (worst > 0) track("web_vital_inp", { value: worst | 0 });
    });

    try {
      lcpObs.observe({ type: "largest-contentful-paint", buffered: true });
    } catch {}
    try {
      clsObs.observe({ type: "layout-shift", buffered: true });
    } catch {}
    try {
      fcpObs.observe({ type: "paint", buffered: true });
    } catch {}
    try {
      inpObs.observe({
        type: "event",
        buffered: true,
        durationThreshold: 40,
      } as PerformanceObserverInit);
    } catch {}

    const onHide = () => {
      track("web_vital_cls", { value: Math.round(cls * 1000) / 1000 });
    };
    window.addEventListener("pagehide", onHide, { once: true });
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });

    return () => {
      lcpObs.disconnect();
      clsObs.disconnect();
      fcpObs.disconnect();
      inpObs.disconnect();
      window.removeEventListener("pagehide", onHide);
    };
  }, []);
}
